import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api.js';
import { IN_RECOVERY_ITEM_STATUSES, statusLabel } from '../utils/statusMeta.js';
import { fmtMoneyCompact } from '../utils/format.js';
import FilterPanel from '../components/FilterPanel.jsx';
import BatchActionBar from '../components/BatchActionBar.jsx';

export default function Dashboard() {
  const [items, setItems] = useState(null);
  // Three independent selection Sets (behavior (a): no cascading).
  const [selCategories, setSelCategories] = useState(() => new Set());
  const [selSubcategories, setSelSubcategories] = useState(() => new Set());
  const [selTags, setSelTags] = useState(() => new Set());
  //select multi
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  
  function toggleSelectMode() {
    setSelectMode((on) => {
      if (on) setSelectedIds(new Set()); // leaving select mode clears selection
      return !on;
    });
  }

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function applyBatch(set) {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      const { updated } = await api.batchUpdateItems(ids, set);
      await reload();              // refresh items with new classifications
      setSelectMode(false);        // exit select mode
      setSelectedIds(new Set());   // clear selection
      // optional: surface a toast/notice
      console.log(`Updated ${updated} item${updated === 1 ? '' : 's'}`);
    } catch (e) {
      setErr(e.message);
    }
  }

  // 'grid' | 'list' — persisted to localStorage
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('ezb_view') || 'grid'; } catch { return 'grid'; }
  });
  const [err, setErr] = useState(null);

  function reload() {
    return api.listItems()
      .then((r) => setItems(r.items))
      .catch((e) => setErr(e.message));
  }

  useEffect(() => { reload(); }, []);

  const totalValue = items?.reduce((s, i) => s + (i.bountyCents ?? 0), 0) ?? 0;
  const inRecovery = items?.filter((i) => IN_RECOVERY_ITEM_STATUSES.includes(i.status)).length ?? 0;

  // category -> Set(subNames) present in the inventory
  const subsByCategory = useMemo(() => {
    const m = new Map();
    for (const it of (items ?? [])) {
      if (!it.category) continue;
      if (!m.has(it.category)) m.set(it.category, new Set());
      if (it.subcategory) m.get(it.category).add(it.subcategory);
    }
    return m;
  }, [items]);

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const noCat = selCategories.size === 0;
    const noSub = selSubcategories.size === 0;
    const noTag = selTags.size === 0;
    if (noCat && noSub && noTag) return list;

    return list.filter((it) => {
      // Taxonomy axis (category + subcategory, treated as union):
      // matches if bare category selected, OR __uncategorized__ selected
      // for category-less items, OR subcategory selected.
      let taxonomyOk = noCat && noSub;
      if (!taxonomyOk) {
        if (it.category && selCategories.has(it.category)) taxonomyOk = true;
        if (!it.category && selCategories.has('__uncategorized__')) taxonomyOk = true;
        if (it.subcategory && selSubcategories.has(it.subcategory)) taxonomyOk = true;
      }
      // Tag axis — intersected with taxonomy.
      const tagOk = noTag || (it.tags ?? []).some((t) => selTags.has(t));
      return taxonomyOk && tagOk;
    });
  }, [items, selCategories, selSubcategories, selTags]);

  // Select-all operates on the CURRENT filtered view (filteredItems).
  const allFilteredSelected =
    filteredItems.length > 0 && filteredItems.every((it) => selectedIds.has(it.itemId));

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const it of filteredItems) next.delete(it.itemId);
      } else {
        for (const it of filteredItems) next.add(it.itemId);
      }
      return next;
    });
  }

  function chooseView(v) {
    setView(v);
    try { localStorage.setItem('ezb_view', v); } catch { /* ignore */ }
  }

  // Clicking a category row.
  function toggleCategory(catName) {
    // Synthetic "Uncategorized" — bare flag only, no subs.
    if (catName === '__uncategorized__') {
      setSelCategories((prev) => {
        const next = new Set(prev);
        next.has(catName) ? next.delete(catName) : next.add(catName);
        return next;
      });
      return;
    }

    const subs = [...(subsByCategory.get(catName) ?? [])];

    // Category with NO subcategories — represent purely via bare flag.
    if (subs.length === 0) {
      setSelCategories((prev) => {
        const next = new Set(prev);
        next.has(catName) ? next.delete(catName) : next.add(catName);
        return next;
      });
      return;
    }

    // Category WITH subcategories.
    // "On" if its bare flag is set OR any of its subs are selected.
    const anyOn = selCategories.has(catName) || subs.some((s) => selSubcategories.has(s));

    setSelCategories((prev) => {
      const next = new Set(prev);
      if (anyOn) next.delete(catName);   // clearing the whole category
      else next.add(catName);            // selecting it (catches no-sub items)
      return next;
    });
    setSelSubcategories((prev) => {
      const next = new Set(prev);
      if (anyOn) { for (const s of subs) next.delete(s); }
      else { for (const s of subs) next.add(s); }
      return next;
    });
  }

  // Clicking a single subcategory toggles just that one, and drops the
  // parent's bare flag (you're now hand-picking subs).
  function toggleSub(subName, parentCat) {
    setSelSubcategories((prev) => {
      const next = new Set(prev);
      next.has(subName) ? next.delete(subName) : next.add(subName);
      return next;
    });
    setSelCategories((prev) => {
      if (!parentCat || !prev.has(parentCat)) return prev;
      const next = new Set(prev);
      next.delete(parentCat);
      return next;
    });
  }

  function toggleTag(tag) {
    setSelTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  function clearFilters() {
    setSelCategories(new Set());
    setSelSubcategories(new Set());
    setSelTags(new Set());
  }

  return (
    <div>
      <div className="row between dash-head">
        <div>
          <span className="eyebrow">Your registry</span>
          <h2 className="mt-6">Inventory</h2>
        </div>
        <Link to="/items/new" className="btn btn-primary">+ New item</Link>
      </div>

      {err && <div className="alert error">{err}</div>}

      {items && (
        <div className="stats">
          <Stat label="Items" value={items.length} />
          <Stat label="Total bounty pool" value={fmtMoneyCompact(totalValue)} accent />
          <Stat label="In recovery" value={inRecovery} />
        </div>
      )}

      {items === null && <SkeletonGrid />}
      {items && items.length === 0 && <Empty />}

      {items && items.length > 0 && (
        <>
          <FilterPanel
            items={items}
            selected={{ categories: selCategories, subcategories: selSubcategories, tags: selTags }}
            onToggleCategory={toggleCategory}
            onToggleSub={toggleSub}
            onToggleTag={toggleTag}
            onClear={clearFilters}
          />

          <div className="row between toolbar">
            <div className="row gap-14">
              <span className="muted tiny">
                {filteredItems.length} item{filteredItems.length === 1 ? '' : 's'}
                {filteredItems.length !== items.length && ` of ${items.length}`}
              </span>
              <button type="button" className="btn btn-ghost btn-compact"
                onClick={toggleSelectMode} aria-pressed={selectMode}>
                {selectMode ? 'Cancel' : 'Select'}
              </button>
              {selectMode && (
                <>
                  <button type="button" className="btn btn-ghost btn-compact"
                    onClick={toggleSelectAll}>
                    {allFilteredSelected ? 'Deselect all' : 'Select all'}
                  </button>
                  <span className="muted tiny">{selectedIds.size} selected</span>
                </>
              )}
            </div>
            
            <div className="row gap-4" role="group" aria-label="View">
              <button type="button" onClick={() => chooseView('grid')}
                className={`btn btn-ghost btn-compact view-btn${!selectMode && view === 'grid' ? ' is-on' : ''}`}
                aria-pressed={!selectMode && view === 'grid'}
                disabled={selectMode}>
                Grid
              </button>
              <button type="button" onClick={() => chooseView('list')}
                className={`btn btn-ghost btn-compact view-btn${selectMode || view === 'list' ? ' is-on' : ''}`}
                aria-pressed={selectMode || view === 'list'}
                disabled={selectMode}>
                List
              </button>
            </div>
          </div>

          

          {filteredItems.length === 0 ? (
            <div className="card center p-48">
              <p className="muted">No items match these filters.</p>
              <button type="button" className="btn btn-ghost mt-8" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : (selectMode || view === 'list') ? (
            <div className="item-list">
              {filteredItems.map((it) => (
                <ItemRow
                  key={it.itemId}
                  item={it}
                  selectMode={selectMode}
                  selected={selectedIds.has(it.itemId)}
                  onToggleSelected={() => toggleSelected(it.itemId)}
                />
              ))}
            </div>
          ) : (
            <div className="items-grid">
              {filteredItems.map((i) => <ItemCard key={i.itemId} item={i} />)}
            </div>
          )}
          {selectMode && selectedIds.size > 0 && (
            <BatchActionBar
              count={selectedIds.size}
              onApply={applyBatch}
              onCancel={toggleSelectMode}
            />
          )}
        </>
      )}
    </div>
  );
}

function ItemRow({ item, selectMode = false, selected = false, onToggleSelected }) {
  const thumbUrl = item.photos?.[0]?.urls?.thumb ?? null;

  const inner = (
    <>
      {selectMode && (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelected}
          onClick={(e) => e.stopPropagation()}
          className="row-check"
          aria-label={`Select ${item.name}`}
        />
      )}
      <div className="thumb row-thumb">
        {thumbUrl && (
          <img src={thumbUrl} alt="" loading="lazy" />
        )}
      </div>
      <div className="row-main">
        <div className="name">{item.name}</div>
        <div className="muted tiny">
          {[item.category, item.subcategory].filter(Boolean).join(' › ') || 'Uncategorized'}
          {item.tags?.length ? ` · ${item.tags.join(', ')}` : ''}
        </div>
      </div>
      <div className="row-end">
        <div className="bounty">{fmtMoneyCompact(item.bountyCents, item.currency)}</div>
        <span className={`status status-${item.status}`}>{statusLabel(item.status)}</span>
      </div>
    </>
  );


  // In select mode the row is a div that toggles selection; otherwise a Link.
  if (selectMode) {
    return (
      <div className={`item-row${selected ? ' is-selected' : ''}`} onClick={onToggleSelected}>
        {inner}
      </div>
    );
  }
  return (
    <Link to={`/items/${item.itemId}`} className="item-row">
      {inner}
    </Link>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="card stat">
      <div className="eyebrow">{label}</div>
      <div className={`stat-value${accent ? ' is-accent' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function ItemCard({ item }) {
  // The list response usually carries signed thumbnail URLs already; only
  // fall back to a per-card sign request for older rows that don't.
  const hydratedThumb = item.photos?.[0]?.urls?.thumb ?? item.photos?.[0]?.urls?.display ?? null;
  const [thumbUrl, setThumbUrl] = useState(hydratedThumb);
  const firstKey = item.photoKeys?.[0];

  useEffect(() => {
    if (hydratedThumb || !firstKey) return;
    let cancelled = false;
    api.signPhotoView(firstKey)
      .then(({ url }) => { if (!cancelled) setThumbUrl(url); })
      .catch(() => { /* leave placeholder on error */ });
    return () => { cancelled = true; };
  }, [firstKey, hydratedThumb]);

  return (
    <Link to={`/items/${item.itemId}`} className="item-card">
      <div className="thumb">
        {thumbUrl && (
          <img src={thumbUrl} alt="" />
        )}
      </div>
      <div className="body">
        <div className="row between mb-8">
          <div>
            <span className="tag-no">No. {item.itemId}</span>
            <h4 className="name">{item.name}</h4>
          </div>
        </div>
        <div className="muted tiny">{item.category}</div>
        <div className="meta">
          <span className={`status status-${item.status}`}>{statusLabel(item.status)}</span>
          <span className="bounty">{fmtMoneyCompact(item.bountyCents, item.currency)}</span>
        </div>
      </div>
    </Link>
  );
}

function SkeletonGrid() {
  return (
    <div className="items-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="item-card is-skeleton" aria-hidden="true">
          <div className="skeleton sk-thumb" />
          <div className="body">
            <div className="skeleton sk-line-lg" />
            <div className="skeleton sk-line-sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Empty() {
  return (
    <div className="card empty">
      <div className="empty-glyph" aria-hidden="true">∅</div>
      <h3 className="mt-16 mb-8">No items yet.</h3>
      <p className="muted mb-24">Begin by adding something worth recovering.</p>
      <Link to="/items/new" className="btn btn-primary">+ Add your first item</Link>
    </div>
  );
}
